import type {
  AssetClassStatisticsRequest,
  CorrelationMatrixData,
  CreateAssetClassRequest,
  CreateProductRequest,
  ProductImportJob,
  ProductImportRow,
  ProductTypeAssessmentMode,
  UpdateAssetClassRequest,
  UpdateProductRequest,
} from "@repo/shared/types";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import * as pcApi from "@/lib/api/portfolio-collections";
import {
  deleteProductDocument,
  getCollectionProductDocuments,
  getDocumentTypesWithDocs,
  refreshMsDocument,
  saveMsDocument,
  saveUploadedDocument,
} from "@/lib/api/product-documents";
import { useInvalidateConfiguredAssessmentTypes } from "./use-configured-assessment-types";
import { useInvalidateFlowCachesForCollectionEdit } from "./use-flow-cache-invalidation";

// Query key factory
const keys = {
  all: ["portfolioCollections"] as const,
  // "detail" leaf segment keeps this key from being a prefix of the sibling
  // keys below — invalidating it must not cascade into products/riskScores/etc.
  detail: (id: string | null) => ["portfolioCollections", id, "detail"] as const,
  settings: (id: string | null) => ["portfolioCollections", id, "settings"] as const,
  currencyFees: (id: string | null) => ["portfolioCollections", id, "currencyFees"] as const,
  products: (id: string | null) => ["portfolioCollections", id, "products"] as const,
  product: (cid: string | null, pid: string | null) =>
    ["portfolioCollections", cid, "products", pid] as const,
  costTypes: (id: string | null) => ["portfolioCollections", id, "costTypes"] as const,
  portfolioCosts: (id: string | null) => ["portfolioCollections", id, "portfolioCosts"] as const,
  assetClasses: (id: string | null) => ["portfolioCollections", id, "assetClasses"] as const,
  selections: (id: string | null, acId: string | null) =>
    ["portfolioCollections", id, "assetClasses", acId, "selections"] as const,
  correlations: (id: string | null) => ["portfolioCollections", id, "correlations"] as const,
  productCorrelations: (id: string | null) =>
    ["portfolioCollections", id, "productCorrelations"] as const,
  productTypes: (id: string | null) => ["portfolioCollections", id, "productTypes"] as const,
  goalTypes: (id: string | null) => ["portfolioCollections", id, "goalTypes"] as const,
  wrapperTypes: (id: string | null) => ["portfolioCollections", id, "wrapperTypes"] as const,
  clientPreferences: (id: string | null) =>
    ["portfolioCollections", id, "clientPreferences"] as const,
  platforms: (id: string | null) => ["portfolioCollections", id, "platforms"] as const,
  mandateTemplates: (id: string | null) =>
    ["portfolioCollections", id, "mandateTemplates"] as const,
  sustainabilityConfig: (id: string | null) =>
    ["portfolioCollections", id, "sustainabilityConfig"] as const,
  productTypeAssessments: (id: string | null, ptId: string | null) =>
    ["portfolioCollections", id, "productTypes", ptId, "assessment"] as const,
  productCostClasses: (id: string | null) =>
    ["portfolioCollections", id, "productCostClasses"] as const,
  portfolios: (id: string | null) => ["portfolioCollections", id, "portfolios"] as const,
  dynamicPortfolios: (id: string | null) =>
    ["portfolioCollections", id, "dynamicPortfolios"] as const,
  /** Under `dynamicPortfolios`, so invalidating the list also refreshes one item. */
  dynamicPortfolio: (cid: string | null, dpid: string | null) =>
    ["portfolioCollections", cid, "dynamicPortfolios", dpid] as const,
  portfolio: (cid: string | null, pid: string | null) =>
    ["portfolioCollections", cid, "portfolios", pid] as const,
  riskScores: (id: string | null) => ["portfolioCollections", id, "riskScores"] as const,
  suitabilityOptions: (id: string | null) =>
    ["portfolioCollections", id, "suitabilityOptions"] as const,
  productDocumentsOverview: (id: string | null) =>
    ["portfolioCollections", id, "productDocumentsOverview"] as const,
  // Per-product document types + their attached documents (product dialog "Documents" tab).
  // Deliberately not nested under keys.product() so a product edit does not cascade into it,
  // and so document-type CRUD can invalidate every product at once by prefix.
  productDocuments: (cid: string | null, pid: string | null) =>
    ["portfolioCollections", cid, "productDocuments", pid] as const,
  productAllocationsOverview: (id: string | null) =>
    ["portfolioCollections", id, "productAllocationsOverview"] as const,
};

// ==========================================
// Query hooks
// ==========================================

export function usePortfolioCollections() {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.all,
    queryFn: pcApi.list,
  });
  return { collections: data ?? [], error, isLoading };
}

export function usePortfolioCollection(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.detail(id),
    queryFn: () => pcApi.getById(id!),
    enabled: !!id,
  });
  return { collection: data ?? null, error, isLoading };
}

export function usePortfolioCollectionSettings(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.settings(id),
    queryFn: () => pcApi.getSettings(id!),
    enabled: !!id,
  });
  return { settings: data ?? null, error, isLoading };
}

export function useCurrencyFees(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.currencyFees(id),
    queryFn: () => pcApi.getCurrencyFees(id!),
    enabled: !!id,
  });
  return { currencyFees: data ?? null, error, isLoading };
}

export function useProducts(collectionId: string | null, search?: string) {
  const normalizedSearch = search?.trim() ?? "";
  const { data, error, isLoading } = useQuery({
    queryKey: [...keys.products(collectionId), normalizedSearch] as const,
    queryFn: () =>
      pcApi.listProducts(
        collectionId!,
        normalizedSearch ? { search: normalizedSearch } : undefined,
      ),
    enabled: !!collectionId,
    placeholderData: keepPreviousData,
  });
  return { products: data ?? [], error, isLoading };
}

export function useCostTypes(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.costTypes(collectionId),
    queryFn: () => pcApi.listCostTypes(collectionId!),
    enabled: !!collectionId,
  });
  return { costTypes: data ?? [], error, isLoading };
}

export function usePortfolioCosts(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.portfolioCosts(collectionId),
    queryFn: () => pcApi.listPortfolioCosts(collectionId!),
    enabled: !!collectionId,
  });
  return { portfolioCosts: data ?? [], error, isLoading };
}

export function useAssetClasses(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.assetClasses(collectionId),
    queryFn: () => pcApi.listAssetClasses(collectionId!),
    enabled: !!collectionId,
  });
  return { assetClasses: data ?? [], error, isLoading };
}

export function useDynamicPortfolios(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.dynamicPortfolios(collectionId),
    queryFn: () => pcApi.listDynamicPortfolios(collectionId!),
    enabled: !!collectionId,
  });
  return { dynamicPortfolios: data ?? [], error, isLoading };
}

export function useDynamicPortfolio(
  collectionId: string | null,
  dynamicPortfolioId: string | null,
) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.dynamicPortfolio(collectionId, dynamicPortfolioId),
    queryFn: () => pcApi.getDynamicPortfolio(collectionId!, dynamicPortfolioId!),
    enabled: !!collectionId && !!dynamicPortfolioId,
  });
  return { dynamicPortfolio: data ?? null, error, isLoading };
}

export function useSelections(collectionId: string | null, assetClassId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.selections(collectionId, assetClassId),
    queryFn: () => pcApi.listSelections(collectionId!, assetClassId!),
    enabled: !!collectionId && !!assetClassId,
  });
  return { selections: data ?? [], error, isLoading };
}

export function useReorderSelection() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      assetClassId,
      productId,
      newRank,
    }: {
      collectionId: string;
      assetClassId: string;
      productId: string;
      newRank: number;
    }) => pcApi.reorderSelection(collectionId, assetClassId, { productId, newRank }),
    // The response is the full re-sorted list — write it into the cache so
    // consecutive moves always start from the server's order.
    onSuccess: (data, { collectionId, assetClassId }) => {
      qc.setQueryData(keys.selections(collectionId, assetClassId), data);
      // Ranks are a direct dynamic-generation input (rank-1 pick per class).
      invalidateFlowCaches();
    },
  });
}

// QP-164 — a fresh `{}` literal as the `data ?? {}` fallback below would be a
// new reference on every render while `data` is undefined (loading, or a
// failed fetch — isLoading turns false but data never resolves). Consumers
// that put this in a useEffect dependency array would then re-run every
// render; once the fetch has failed, that useEffect's own `!loading` guard no
// longer blocks it, so it can setState in a loop. One shared reference keeps
// the fallback stable across renders.
const EMPTY_CORRELATIONS: CorrelationMatrixData = {};

export function useCorrelationMatrix(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.correlations(collectionId),
    queryFn: () => pcApi.getCorrelations(collectionId!),
    enabled: !!collectionId,
  });
  return { correlations: data ?? EMPTY_CORRELATIONS, error, isLoading };
}

export function useProductCorrelationMatrix(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.productCorrelations(collectionId),
    queryFn: () => pcApi.getProductCorrelations(collectionId!),
    enabled: !!collectionId,
  });
  return { correlations: data ?? EMPTY_CORRELATIONS, error, isLoading };
}

export function useProductTypes(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.productTypes(collectionId),
    queryFn: () => pcApi.listProductTypes(collectionId!),
    enabled: !!collectionId,
  });
  return { productTypes: data ?? [], error, isLoading };
}

export function useCollectionRiskScores(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.riskScores(collectionId),
    queryFn: () => pcApi.getCollectionRiskScores(collectionId!),
    enabled: !!collectionId,
  });
  return { riskScores: data ?? [], error, isLoading };
}

export function usePortfolio(collectionId: string | null, portfolioId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.portfolio(collectionId, portfolioId),
    queryFn: () => pcApi.getPortfolio(collectionId!, portfolioId!),
    enabled: !!collectionId && !!portfolioId,
  });
  return { portfolio: data ?? null, error, isLoading };
}

export function useSuitabilityOptions(
  collectionId: string | null,
  productId: string | null = null,
) {
  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: productId
      ? ([...keys.suitabilityOptions(collectionId), productId] as const)
      : keys.suitabilityOptions(collectionId),
    queryFn: () =>
      productId
        ? pcApi.getSuitabilityOptions(collectionId!, productId)
        : pcApi.getNewProductSuitabilityOptions(collectionId!),
    enabled: !!collectionId,
  });
  return {
    suitabilityOptions: data ?? {
      productTypesEnabled: false,
      productTypes: [],
      timeHorizons: [],
      riskLevels: [],
      scoreRange: null,
      matrixScores: [],
    },
    error,
    isLoading,
    // QL-12 — the create dialog needs this to tell a stale cached payload from a
    // settled one before it pre-selects the default product type.
    isFetching,
  };
}

export function useInstrumentMetadata(ticker: string | null, dataset: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["instrumentMetadata", ticker, dataset] as const,
    queryFn: () => pcApi.getInstrumentMetadata({ ticker: ticker!, dataset: dataset! }),
    enabled: !!ticker && !!dataset,
  });
  return { metadata: data ?? null, error, isLoading };
}

export function useProductAnalysis(collectionId: string | null, productId: string | null) {
  return useQuery({
    queryKey: ["portfolioCollections", collectionId, "products", productId, "analysis"],
    queryFn: () => pcApi.getProductAnalysis(collectionId!, productId!),
    enabled: !!collectionId && !!productId,
    staleTime: 5 * 60 * 1000,
  });
}

const DAY_MS = 24 * 60 * 60 * 1000;

// X-Ray is keyed by ISIN (+ language) and cached for 24h, so the same
// instrument reuses the Morningstar result across products/collections.
export function useProductXray(
  collectionId: string | null,
  productId: string | null,
  isin: string | null,
  lang: string,
) {
  return useQuery({
    queryKey: ["productXray", isin, lang],
    queryFn: () => pcApi.getProductXray(collectionId!, productId!, lang),
    enabled: !!collectionId && !!productId && !!isin,
    staleTime: DAY_MS,
    gcTime: DAY_MS,
  });
}

export function useProductDocumentsOverview(collectionId: string | null, search?: string) {
  const normalizedSearch = search?.trim() ?? "";
  const { data, error, isLoading } = useQuery({
    queryKey: [...keys.productDocumentsOverview(collectionId), normalizedSearch] as const,
    queryFn: () =>
      getCollectionProductDocuments(
        collectionId!,
        normalizedSearch ? { search: normalizedSearch } : undefined,
      ),
    enabled: !!collectionId,
    placeholderData: keepPreviousData,
  });
  return {
    overview: data ?? { documentTypes: [], products: [] },
    error,
    isLoading,
  };
}

export function useProductDocumentTypesWithDocs(
  collectionId: string | null,
  productId: string | null,
) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.productDocuments(collectionId, productId),
    queryFn: () => getDocumentTypesWithDocs(collectionId!, productId!),
    enabled: !!collectionId && !!productId,
  });
  return { documentTypes: data ?? [], error, isLoading };
}

export function useProductAllocationsOverview(collectionId: string | null, search?: string) {
  const normalizedSearch = search?.trim() ?? "";
  const { data, error, isLoading } = useQuery({
    queryKey: [...keys.productAllocationsOverview(collectionId), normalizedSearch] as const,
    queryFn: () =>
      pcApi.listProductAllocations(
        collectionId!,
        normalizedSearch ? { search: normalizedSearch } : undefined,
      ),
    enabled: !!collectionId,
    placeholderData: keepPreviousData,
  });
  return { allocations: data ?? [], error, isLoading };
}

// ==========================================
// Collection mutation hooks
// ==========================================

export function useCreateCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pcApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useCreateCollectionFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pcApi.createFromTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useUpdateCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof pcApi.update>[1] }) =>
      pcApi.update(id, data),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useDeleteCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pcApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useToggleCollectionActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pcApi.toggleActive,
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

/**
 * QL-375 — the collection's mandate switch and rules. Invalidates the settings
 * query (the rules card reads it) and the detail query (the layout gates the
 * Mandates tab and its warning on it).
 */
// ── Mandate templates (QL-375) ───────────────────────────────────────────────
// Every write invalidates the templates list AND the collection detail: the
// Mandates tab's warnings (scores without a template, untagged templates,
// inactive products) live on the detail payload.

export function useMandateTemplates(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.mandateTemplates(collectionId),
    queryFn: () => pcApi.listMandateTemplates(collectionId!),
    enabled: !!collectionId,
  });
  return { templates: data ?? [], error, isLoading };
}

function invalidateMandateTemplates(qc: ReturnType<typeof useQueryClient>, collectionId: string) {
  qc.invalidateQueries({ queryKey: keys.mandateTemplates(collectionId) });
  qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
  // A template write re-resolves every goal on the collection's open
  // instances (server side), so their mandate panels re-read too (PR 598 review).
  qc.invalidateQueries({ queryKey: ["flowInstances"] });
}

export function useCreateMandateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: Parameters<typeof pcApi.createMandateTemplate>[1];
    }) => pcApi.createMandateTemplate(collectionId, data),
    onSuccess: (_d, { collectionId }) => invalidateMandateTemplates(qc, collectionId),
  });
}

export function useUpdateMandateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      templateId,
      data,
    }: {
      collectionId: string;
      templateId: string;
      data: Parameters<typeof pcApi.updateMandateTemplate>[2];
    }) => pcApi.updateMandateTemplate(collectionId, templateId, data),
    onSuccess: (_d, { collectionId }) => invalidateMandateTemplates(qc, collectionId),
  });
}

export function useToggleMandateTemplateActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, templateId }: { collectionId: string; templateId: string }) =>
      pcApi.toggleMandateTemplateActive(collectionId, templateId),
    onSuccess: (_d, { collectionId }) => invalidateMandateTemplates(qc, collectionId),
  });
}

export function useDeleteMandateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, templateId }: { collectionId: string; templateId: string }) =>
      pcApi.deleteMandateTemplate(collectionId, templateId),
    onSuccess: (_d, { collectionId }) => invalidateMandateTemplates(qc, collectionId),
  });
}

export function useUpdateMandateSettings() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof pcApi.updateMandateSettings>[1];
    }) => pcApi.updateMandateSettings(id, data),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: keys.settings(id) });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      // The switch and the rules change every instance's mandate check.
      invalidateFlowCaches();
    },
  });
}

export function useUpdateCollectionSettings() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof pcApi.updateSettings>[1] }) =>
      pcApi.updateSettings(id, data),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: keys.settings(id) });
      // The layout gates the Dynamic Portfolios tab on the detail query's
      // dynamicPortfoliosEnabled — refresh it so the tab reacts immediately.
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      // QP-164 — a calculations-mode switch wipes the now-inactive mode's
      // correlation matrix and expected return/volatility values server-side
      // (asset classes when switching away from "asset_classes"; products
      // when switching away from "products"); refresh all four so the Asset
      // Classes, Products, and Correlations tabs don't show stale pre-switch
      // values.
      qc.invalidateQueries({ queryKey: keys.assetClasses(id) });
      qc.invalidateQueries({ queryKey: keys.correlations(id) });
      qc.invalidateQueries({ queryKey: keys.products(id) });
      qc.invalidateQueries({ queryKey: keys.productCorrelations(id) });
      // QL-383 — suitability options carry productTypesEnabled and an empty
      // product type list while the setting is off; the product dialog reads
      // them, so refresh after a toggle.
      qc.invalidateQueries({ queryKey: keys.suitabilityOptions(id) });
      // Requirement-setting enables and bank-rate changes flag instances for
      // snapshot regeneration.
      invalidateFlowCaches();
    },
  });
}

export function useUpdateCurrencyFees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof pcApi.updateCurrencyFees>[1];
    }) => pcApi.updateCurrencyFees(id, data),
    onSuccess: (_d, { id }) => qc.invalidateQueries({ queryKey: keys.currencyFees(id) }),
  });
}

// ==========================================
// Product mutation hooks
// ==========================================

export function useCreateProduct() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ collectionId, data }: { collectionId: string; data: CreateProductRequest }) =>
      pcApi.createProduct(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productDocumentsOverview(collectionId) });
      // A new product can put a previously unused product type in use.
      qc.invalidateQueries({ queryKey: keys.suitabilityOptions(collectionId) });
      // Product platform tags feed platform.productIds (QP-226).
      qc.invalidateQueries({ queryKey: keys.platforms(collectionId) });
      // Collection detail embeds wrapperTypeStatus (tab warning icons).
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      invalidateFlowCaches();
    },
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      productId,
      data,
    }: {
      collectionId: string;
      productId: string;
      data: UpdateProductRequest;
    }) => pcApi.updateProduct(collectionId, productId, data),
    onSuccess: (_d, { collectionId, productId }) => {
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      qc.invalidateQueries({ queryKey: keys.product(collectionId, productId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productDocumentsOverview(collectionId) });
      qc.invalidateQueries({ queryKey: keys.suitabilityOptions(collectionId) });
      // Product platform tags feed platform.productIds (QP-226).
      qc.invalidateQueries({ queryKey: keys.platforms(collectionId) });
      // Dynamic portfolios embed the product's name and ISIN.
      qc.invalidateQueries({ queryKey: keys.dynamicPortfolios(collectionId) });
      // Collection detail embeds wrapperTypeStatus (tab warning icons).
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      // QL-375 — template lines read the product's name, ISIN and platforms.
      qc.invalidateQueries({ queryKey: keys.mandateTemplates(collectionId) });
      invalidateFlowCaches();
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ collectionId, productId }: { collectionId: string; productId: string }) =>
      pcApi.deleteProduct(collectionId, productId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productDocumentsOverview(collectionId) });
      // Cascade drops the product's platform memberships (QP-226).
      qc.invalidateQueries({ queryKey: keys.platforms(collectionId) });
      // Collection detail embeds wrapperTypeStatus (tab warning icons).
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      qc.invalidateQueries({ queryKey: keys.mandateTemplates(collectionId) });
      invalidateFlowCaches();
    },
  });
}

export function useToggleProductActive() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ collectionId, productId }: { collectionId: string; productId: string }) =>
      pcApi.toggleProductActive(collectionId, productId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
      qc.invalidateQueries({ queryKey: keys.suitabilityOptions(collectionId) });
      qc.invalidateQueries({ queryKey: keys.platforms(collectionId) });
      // QL-375 — a template line on an inactive product is a warning on the
      // collection detail and a flag on the template's line.
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      qc.invalidateQueries({ queryKey: keys.mandateTemplates(collectionId) });
      invalidateFlowCaches();
    },
  });
}

export function useGetInstrumentMetadata() {
  return useMutation({
    mutationFn: pcApi.getInstrumentMetadata,
  });
}

// ==========================================
// Portfolio mutation hooks
// ==========================================

export function useCreatePortfolio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: Parameters<typeof pcApi.createPortfolio>[1];
    }) => pcApi.createPortfolio(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.portfolios(collectionId) });
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
    },
  });
}

export function useUpdatePortfolio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      portfolioId,
      data,
    }: {
      collectionId: string;
      portfolioId: string;
      data: Parameters<typeof pcApi.updatePortfolio>[2];
    }) => pcApi.updatePortfolio(collectionId, portfolioId, data),
    onSuccess: (_d, { collectionId, portfolioId }) => {
      qc.invalidateQueries({ queryKey: keys.portfolios(collectionId) });
      qc.invalidateQueries({ queryKey: keys.portfolio(collectionId, portfolioId) });
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
    },
  });
}

export function useDeletePortfolio() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ collectionId, portfolioId }: { collectionId: string; portfolioId: string }) =>
      pcApi.deletePortfolio(collectionId, portfolioId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.portfolios(collectionId) });
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
      // Model-portfolio edits wipe the snapshots of instances using it.
      invalidateFlowCaches();
    },
  });
}

export function useCreateDynamicPortfolio() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: Parameters<typeof pcApi.createDynamicPortfolio>[1];
    }) => pcApi.createDynamicPortfolio(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      // The definition itself is a dynamic-generation input (QP-185).
      invalidateFlowCaches();
      return qc.invalidateQueries({ queryKey: keys.dynamicPortfolios(collectionId) });
    },
  });
}

export function useUpdateDynamicPortfolio() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      dynamicPortfolioId,
      data,
    }: {
      collectionId: string;
      dynamicPortfolioId: string;
      data: Parameters<typeof pcApi.updateDynamicPortfolio>[2];
    }) => pcApi.updateDynamicPortfolio(collectionId, dynamicPortfolioId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.dynamicPortfolios(collectionId) });
      // The definition itself is a dynamic-generation input (QP-185).
      invalidateFlowCaches();
    },
  });
}

export function useDeleteDynamicPortfolio() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      dynamicPortfolioId,
    }: {
      collectionId: string;
      dynamicPortfolioId: string;
    }) => pcApi.deleteDynamicPortfolio(collectionId, dynamicPortfolioId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.dynamicPortfolios(collectionId) });
      // The definition itself is a dynamic-generation input (QP-185).
      invalidateFlowCaches();
    },
  });
}

export function useTogglePortfolioActive() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ collectionId, portfolioId }: { collectionId: string; portfolioId: string }) =>
      pcApi.togglePortfolioActive(collectionId, portfolioId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.portfolios(collectionId) });
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
      // Model-portfolio edits wipe the snapshots of instances using it.
      invalidateFlowCaches();
    },
  });
}

// ==========================================
// Portfolio Product mutation hooks
// ==========================================

export function useAddPortfolioProduct() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      portfolioId,
      data,
    }: {
      collectionId: string;
      portfolioId: string;
      data: Parameters<typeof pcApi.addPortfolioProduct>[2];
    }) => pcApi.addPortfolioProduct(collectionId, portfolioId, data),
    onSuccess: (_d, { collectionId, portfolioId }) => {
      qc.invalidateQueries({ queryKey: keys.portfolio(collectionId, portfolioId) });
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
      // Model-portfolio edits wipe the snapshots of instances using it.
      invalidateFlowCaches();
    },
  });
}

export function useUpdatePortfolioProduct() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      portfolioId,
      ppId,
      data,
    }: {
      collectionId: string;
      portfolioId: string;
      ppId: string;
      data: Parameters<typeof pcApi.updatePortfolioProduct>[3];
    }) => pcApi.updatePortfolioProduct(collectionId, portfolioId, ppId, data),
    onSuccess: (_d, { collectionId, portfolioId }) => {
      qc.invalidateQueries({ queryKey: keys.portfolio(collectionId, portfolioId) });
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
      // Model-portfolio edits wipe the snapshots of instances using it.
      invalidateFlowCaches();
    },
  });
}

export function useDeletePortfolioProduct() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      portfolioId,
      ppId,
    }: {
      collectionId: string;
      portfolioId: string;
      ppId: string;
    }) => pcApi.deletePortfolioProduct(collectionId, portfolioId, ppId),
    onSuccess: (_d, { collectionId, portfolioId }) => {
      qc.invalidateQueries({ queryKey: keys.portfolio(collectionId, portfolioId) });
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
      // Model-portfolio edits wipe the snapshots of instances using it.
      invalidateFlowCaches();
    },
  });
}

// ==========================================
// Dynamic Portfolio Product mutation hooks
// ==========================================

// Each returns the list invalidation, so `mutateAsync` resolves once the
// definitions on screen are fresh — the grid saves cell after cell on that.

export function useAddDynamicPortfolioProduct() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      dynamicPortfolioId,
      data,
    }: {
      collectionId: string;
      dynamicPortfolioId: string;
      data: Parameters<typeof pcApi.addDynamicPortfolioProduct>[2];
    }) => pcApi.addDynamicPortfolioProduct(collectionId, dynamicPortfolioId, data),
    onSuccess: (_d, { collectionId }) => {
      // The definition itself is a dynamic-generation input (QP-185).
      invalidateFlowCaches();
      return qc.invalidateQueries({ queryKey: keys.dynamicPortfolios(collectionId) });
    },
  });
}

export function useUpdateDynamicPortfolioProduct() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      dynamicPortfolioId,
      dynamicPortfolioProductId,
      data,
    }: {
      collectionId: string;
      dynamicPortfolioId: string;
      dynamicPortfolioProductId: string;
      data: Parameters<typeof pcApi.updateDynamicPortfolioProduct>[3];
    }) =>
      pcApi.updateDynamicPortfolioProduct(
        collectionId,
        dynamicPortfolioId,
        dynamicPortfolioProductId,
        data,
      ),
    onSuccess: (_d, { collectionId }) => {
      // The definition itself is a dynamic-generation input (QP-185).
      invalidateFlowCaches();
      return qc.invalidateQueries({ queryKey: keys.dynamicPortfolios(collectionId) });
    },
  });
}

export function useDeleteDynamicPortfolioProduct() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      dynamicPortfolioId,
      dynamicPortfolioProductId,
    }: {
      collectionId: string;
      dynamicPortfolioId: string;
      dynamicPortfolioProductId: string;
    }) =>
      pcApi.deleteDynamicPortfolioProduct(
        collectionId,
        dynamicPortfolioId,
        dynamicPortfolioProductId,
      ),
    onSuccess: (_d, { collectionId }) => {
      // The definition itself is a dynamic-generation input (QP-185).
      invalidateFlowCaches();
      return qc.invalidateQueries({ queryKey: keys.dynamicPortfolios(collectionId) });
    },
  });
}

// ==========================================
// Cost Type mutation hooks
// ==========================================

export function useCreateCostType() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ collectionId, data }: { collectionId: string; data: Record<string, unknown> }) =>
      pcApi.createCostType(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.costTypes(collectionId) });
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      // QL-20 — open flows read each fee's value and advisorOverride mode.
      invalidateFlowCaches();
    },
  });
}

export function useUpdateCostType() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      ctId,
      data,
    }: {
      collectionId: string;
      ctId: string;
      data: Record<string, unknown>;
    }) => pcApi.updateCostType(collectionId, ctId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.costTypes(collectionId) });
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      // QL-20 — open flows read each fee's value and advisorOverride mode.
      invalidateFlowCaches();
    },
  });
}

export function useDeleteCostType() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ collectionId, ctId }: { collectionId: string; ctId: string }) =>
      pcApi.deleteCostType(collectionId, ctId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.costTypes(collectionId) });
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      // QL-20 — open flows read each fee's value and advisorOverride mode.
      invalidateFlowCaches();
    },
  });
}

// ==========================================
// Portfolio Cost mutation hooks
// ==========================================

export function useCreatePortfolioCost() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ collectionId, data }: { collectionId: string; data: Record<string, unknown> }) =>
      pcApi.createPortfolioCost(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.portfolioCosts(collectionId) });
      // QL-20 — open flows read each fee's value and advisorOverride mode.
      invalidateFlowCaches();
    },
  });
}

export function useUpdatePortfolioCost() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      pcId,
      data,
    }: {
      collectionId: string;
      pcId: string;
      data: Record<string, unknown>;
    }) => pcApi.updatePortfolioCost(collectionId, pcId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.portfolioCosts(collectionId) });
      // QL-20 — open flows read each fee's value and advisorOverride mode.
      invalidateFlowCaches();
    },
  });
}

export function useDeletePortfolioCost() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ collectionId, pcId }: { collectionId: string; pcId: string }) =>
      pcApi.deletePortfolioCost(collectionId, pcId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.portfolioCosts(collectionId) });
      // QL-20 — open flows read each fee's value and advisorOverride mode.
      invalidateFlowCaches();
    },
  });
}

// ==========================================
// Asset Class mutation hooks
// ==========================================

export function useCreateAssetClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, data }: { collectionId: string; data: CreateAssetClassRequest }) =>
      pcApi.createAssetClass(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.assetClasses(collectionId) });
      qc.invalidateQueries({ queryKey: keys.correlations(collectionId) });
    },
  });
}

export function useUpdateAssetClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      acId,
      data,
    }: {
      collectionId: string;
      acId: string;
      data: UpdateAssetClassRequest;
    }) => pcApi.updateAssetClass(collectionId, acId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.assetClasses(collectionId) });
      qc.invalidateQueries({ queryKey: keys.correlations(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
    },
  });
}

export function useDeleteAssetClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, acId }: { collectionId: string; acId: string }) =>
      pcApi.deleteAssetClass(collectionId, acId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.assetClasses(collectionId) });
      qc.invalidateQueries({ queryKey: keys.correlations(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productAllocationsOverview(collectionId) });
    },
  });
}

export function useGetAssetClassStatistics() {
  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: AssetClassStatisticsRequest;
    }) => pcApi.getAssetClassStatistics(collectionId, data),
  });
}

// ==========================================
// Correlation mutation hooks
// ==========================================

export function useSaveCorrelations() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: Parameters<typeof pcApi.saveCorrelations>[1];
    }) => pcApi.saveCorrelations(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.correlations(collectionId) });
      // QL-655 — the save drops the forecast-family snapshots of the
      // collection's editable instances; cached flow data must follow.
      invalidateFlowCaches();
    },
  });
}

export function useFetchApiCorrelations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, data }: { collectionId: string; data: Record<string, unknown> }) =>
      pcApi.fetchApiCorrelations(collectionId, data),
    onSuccess: (_d, { collectionId }) =>
      qc.invalidateQueries({ queryKey: keys.correlations(collectionId) }),
  });
}

export function useSaveProductCorrelations() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: Parameters<typeof pcApi.saveProductCorrelations>[1];
    }) => pcApi.saveProductCorrelations(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.productCorrelations(collectionId) });
      // QL-655 — the save drops the forecast-family snapshots of the
      // collection's editable instances; cached flow data must follow.
      invalidateFlowCaches();
    },
  });
}

// ==========================================
// Product Type mutation hooks
// ==========================================

export function useCreateProductType() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  const invalidateConfiguredTypes = useInvalidateConfiguredAssessmentTypes();
  return useMutation({
    mutationFn: ({ collectionId, data }: { collectionId: string; data: Record<string, unknown> }) =>
      pcApi.createProductType(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.productTypes(collectionId) });
      invalidateFlowCaches();
      // QL-361 — the `__pa_…` condition sources are built from this list.
      invalidateConfiguredTypes();
    },
  });
}

export function useUpdateProductType() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  const invalidateConfiguredTypes = useInvalidateConfiguredAssessmentTypes();
  return useMutation({
    mutationFn: ({
      collectionId,
      ptId,
      data,
    }: {
      collectionId: string;
      ptId: string;
      data: Record<string, unknown>;
    }) => pcApi.updateProductType(collectionId, ptId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.productTypes(collectionId) });
      qc.invalidateQueries({ queryKey: keys.suitabilityOptions(collectionId) });
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      // The product type is the "has experience" leg of checkProductSuitability.
      invalidateFlowCaches();
      // QL-361 — the `__pa_…` condition sources are built from this list.
      invalidateConfiguredTypes();
    },
  });
}

export function useDeleteProductType() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  const invalidateConfiguredTypes = useInvalidateConfiguredAssessmentTypes();
  return useMutation({
    mutationFn: ({ collectionId, ptId }: { collectionId: string; ptId: string }) =>
      pcApi.deleteProductType(collectionId, ptId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.productTypes(collectionId) });
      qc.invalidateQueries({ queryKey: keys.suitabilityOptions(collectionId) });
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      // The product type is the "has experience" leg of checkProductSuitability.
      invalidateFlowCaches();
      // QL-361 — the `__pa_…` condition sources are built from this list.
      invalidateConfiguredTypes();
    },
  });
}

export function useToggleProductTypeActive() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  const invalidateConfiguredTypes = useInvalidateConfiguredAssessmentTypes();
  return useMutation({
    mutationFn: ({ collectionId, ptId }: { collectionId: string; ptId: string }) =>
      pcApi.toggleProductTypeActive(collectionId, ptId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.productTypes(collectionId) });
      qc.invalidateQueries({ queryKey: keys.suitabilityOptions(collectionId) });
      // The product type is the "has experience" leg of checkProductSuitability.
      invalidateFlowCaches();
      // QL-361 — the `__pa_…` condition sources are built from this list.
      invalidateConfiguredTypes();
    },
  });
}

export function useToggleProductTypeDefault() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, ptId }: { collectionId: string; ptId: string }) =>
      pcApi.toggleProductTypeDefault(collectionId, ptId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.productTypes(collectionId) });
      qc.invalidateQueries({ queryKey: keys.suitabilityOptions(collectionId) });
      // No flow-cache invalidation: the default flag only pre-selects a type on
      // product creation, it is not an input to checkProductSuitability.
    },
  });
}

// ==========================================
// Goal Type hooks (QP-187)
// ==========================================

export function useGoalTypes(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.goalTypes(collectionId),
    queryFn: () => pcApi.listGoalTypes(collectionId!),
    enabled: !!collectionId,
  });
  return { goalTypes: data ?? [], error, isLoading };
}

export function useCreateGoalType() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: Parameters<typeof pcApi.createGoalType>[1];
    }) => pcApi.createGoalType(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.goalTypes(collectionId) });
      // The Mandates tab's "no goal types" warning lives on the detail.
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      // A goal type's mandate three-way changes every instance's mandate check.
      invalidateFlowCaches();
    },
  });
}

export function useUpdateGoalType() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      gtId,
      data,
    }: {
      collectionId: string;
      gtId: string;
      data: Parameters<typeof pcApi.updateGoalType>[2];
    }) => pcApi.updateGoalType(collectionId, gtId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.goalTypes(collectionId) });
      // The Mandates tab's "no goal types" warning lives on the detail.
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      // A goal type's mandate three-way changes every instance's mandate check.
      invalidateFlowCaches();
    },
  });
}

export function useDeleteGoalType() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ collectionId, gtId }: { collectionId: string; gtId: string }) =>
      pcApi.deleteGoalType(collectionId, gtId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.goalTypes(collectionId) });
      // The Mandates tab's "no goal types" warning lives on the detail.
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      // A goal type's mandate three-way changes every instance's mandate check.
      invalidateFlowCaches();
    },
  });
}

export function useReorderGoalTypes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, orderedIds }: { collectionId: string; orderedIds: string[] }) =>
      pcApi.reorderGoalTypes(collectionId, orderedIds),
    onSuccess: (_d, { collectionId }) =>
      qc.invalidateQueries({ queryKey: keys.goalTypes(collectionId) }),
  });
}

// ==========================================
// Wrapper Type hooks
// ==========================================

export function useWrapperTypes(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.wrapperTypes(collectionId),
    queryFn: () => pcApi.listWrapperTypes(collectionId!),
    enabled: !!collectionId,
  });
  return { wrapperTypes: data ?? [], error, isLoading };
}

export function useCreateWrapperType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: Parameters<typeof pcApi.createWrapperType>[1];
    }) => pcApi.createWrapperType(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.wrapperTypes(collectionId) });
      // Collection detail embeds wrapperTypeStatus (tab warning icons).
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
    },
  });
}

export function useUpdateWrapperType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      wtId,
      data,
    }: {
      collectionId: string;
      wtId: string;
      data: Parameters<typeof pcApi.updateWrapperType>[2];
    }) => pcApi.updateWrapperType(collectionId, wtId, data),
    // Product payloads embed wrapperTypeIds and the dialog shows wrapper
    // names, so product caches go stale together with the wrapper-type list.
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.wrapperTypes(collectionId) });
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      // Collection detail embeds wrapperTypeStatus (tab warning icons).
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
    },
  });
}

export function useDeleteWrapperType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, wtId }: { collectionId: string; wtId: string }) =>
      pcApi.deleteWrapperType(collectionId, wtId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.wrapperTypes(collectionId) });
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      // Collection detail embeds wrapperTypeStatus (tab warning icons).
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
    },
  });
}

export function useToggleWrapperTypeActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, wtId }: { collectionId: string; wtId: string }) =>
      pcApi.toggleWrapperTypeActive(collectionId, wtId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.wrapperTypes(collectionId) });
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      // Collection detail embeds wrapperTypeStatus (tab warning icons).
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
    },
  });
}

// ==========================================
// Client Preference hooks (QP-224)
// ==========================================

export function useClientPreferences(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.clientPreferences(collectionId),
    queryFn: () => pcApi.listClientPreferences(collectionId!),
    enabled: !!collectionId,
  });
  return { clientPreferences: data ?? [], error, isLoading };
}

export function useCreateClientPreference() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: Parameters<typeof pcApi.createClientPreference>[1];
    }) => pcApi.createClientPreference(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.clientPreferences(collectionId) });
      // Preference definitions are a matching input — the service wipes the
      // client-preference snapshots of instances on this collection.
      invalidateFlowCaches();
    },
  });
}

export function useUpdateClientPreference() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      prefId,
      data,
    }: {
      collectionId: string;
      prefId: string;
      data: Parameters<typeof pcApi.updateClientPreference>[2];
    }) => pcApi.updateClientPreference(collectionId, prefId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.clientPreferences(collectionId) });
      // Preference definitions are a matching input — the service wipes the
      // client-preference snapshots of instances on this collection.
      invalidateFlowCaches();
    },
  });
}

export function useDeleteClientPreference() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ collectionId, prefId }: { collectionId: string; prefId: string }) =>
      pcApi.deleteClientPreference(collectionId, prefId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.clientPreferences(collectionId) });
      // Preference definitions are a matching input — the service wipes the
      // client-preference snapshots of instances on this collection.
      invalidateFlowCaches();
    },
  });
}

// ==========================================
// Platform hooks (QP-195)
// ==========================================

// --- Sustainability config (QP-179) ---

export function useSustainabilityConfig(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.sustainabilityConfig(collectionId),
    queryFn: () => pcApi.getSustainabilityConfig(collectionId!),
    enabled: !!collectionId,
  });
  return { config: data?.config ?? null, error, isLoading };
}

export function useUpdateSustainabilityConfig() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      config,
    }: {
      collectionId: string;
      config: Parameters<typeof pcApi.updateSustainabilityConfig>[1];
    }) => pcApi.updateSustainabilityConfig(collectionId, config),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.sustainabilityConfig(collectionId) });
      // Changed matching inputs flag instances for snapshot regeneration.
      invalidateFlowCaches();
    },
  });
}

export function usePlatforms(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.platforms(collectionId),
    queryFn: () => pcApi.listPlatforms(collectionId!),
    enabled: !!collectionId,
  });
  return { platforms: data ?? [], error, isLoading };
}

export function useCreatePlatform() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: Parameters<typeof pcApi.createPlatform>[1];
    }) => pcApi.createPlatform(collectionId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.platforms(collectionId) });
      // Platform membership edits feed product.platformIds (QP-226).
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      // Collection detail embeds platformStatus (tab warning icons).
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
    },
  });
}

export function useUpdatePlatform() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({
      collectionId,
      platformId,
      data,
    }: {
      collectionId: string;
      platformId: string;
      data: Parameters<typeof pcApi.updatePlatform>[2];
    }) => pcApi.updatePlatform(collectionId, platformId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.platforms(collectionId) });
      // Platform membership edits feed product.platformIds (QP-226).
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      // Collection detail embeds platformStatus (tab warning icons).
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      // Membership also filters dynamic-generation candidates (QP-185).
      invalidateFlowCaches();
    },
  });
}

export function useDeletePlatform() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  return useMutation({
    mutationFn: ({ collectionId, platformId }: { collectionId: string; platformId: string }) =>
      pcApi.deletePlatform(collectionId, platformId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.platforms(collectionId) });
      // Cascade drops the platform's product memberships (QP-226).
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      // …and its dynamic-portfolio assignments (QL-584).
      qc.invalidateQueries({ queryKey: keys.dynamicPortfolios(collectionId) });
      // Collection detail embeds platformStatus (tab warning icons).
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      // Members stop being candidates for goals on this platform (QP-185).
      invalidateFlowCaches();
    },
  });
}

export function useReorderPlatforms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, orderedIds }: { collectionId: string; orderedIds: string[] }) =>
      pcApi.reorderPlatforms(collectionId, orderedIds),
    onSuccess: (_d, { collectionId }) =>
      qc.invalidateQueries({ queryKey: keys.platforms(collectionId) }),
  });
}

// ==========================================
// Product Type Assessment hooks
// ==========================================

export function useProductTypeAssessments(
  collectionId: string | null,
  productTypeId: string | null,
) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.productTypeAssessments(collectionId, productTypeId),
    queryFn: () => pcApi.getProductTypeAssessments(collectionId!, productTypeId!),
    enabled: !!collectionId && !!productTypeId,
  });
  return { assessments: data ?? [], error, isLoading };
}

// The product-types list carries each type's enabled modes for its badge, and
// the assessment key is *longer* than the list key, so prefix matching would
// never reach it — both keys have to be invalidated explicitly.
function invalidateAssessment(qc: ReturnType<typeof useQueryClient>) {
  return async ({ collectionId, ptId }: { collectionId: string; ptId: string }) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: keys.productTypeAssessments(collectionId, ptId) }),
      qc.invalidateQueries({ queryKey: keys.productTypes(collectionId) }),
    ]);
  };
}

export function useUpsertProductTypeAssessment() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  const invalidateConfiguredTypes = useInvalidateConfiguredAssessmentTypes();
  return useMutation({
    mutationFn: ({
      collectionId,
      ptId,
      data,
    }: {
      collectionId: string;
      ptId: string;
      data: Parameters<typeof pcApi.upsertProductTypeAssessment>[2];
    }) => pcApi.upsertProductTypeAssessment(collectionId, ptId, data),
    onSuccess: async (_d, vars) => {
      await invalidateAssessment(qc)(vars);
      // The assessment drives the "has experience" suitability verdict.
      invalidateFlowCaches();
      // QL-361 — the `__pa_…` condition sources are built from this list.
      invalidateConfiguredTypes();
    },
  });
}

export function useDeleteProductTypeAssessment() {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  const invalidateConfiguredTypes = useInvalidateConfiguredAssessmentTypes();
  return useMutation({
    mutationFn: ({
      collectionId,
      ptId,
      mode,
    }: {
      collectionId: string;
      ptId: string;
      mode: ProductTypeAssessmentMode;
    }) => pcApi.deleteProductTypeAssessment(collectionId, ptId, mode),
    onSuccess: async (_d, vars) => {
      await invalidateAssessment(qc)(vars);
      // The assessment drives the "has experience" suitability verdict.
      invalidateFlowCaches();
      // QL-361 — the `__pa_…` condition sources are built from this list.
      invalidateConfiguredTypes();
    },
  });
}

// ==========================================
// Product Cost Class hooks
// ==========================================

export function useProductCostClasses(collectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.productCostClasses(collectionId),
    queryFn: () => pcApi.listProductCostClasses(collectionId!),
    enabled: !!collectionId,
  });
  return { productCostClasses: data ?? [], error, isLoading };
}

export function useCreateProductCostClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, data }: { collectionId: string; data: Record<string, unknown> }) =>
      pcApi.createProductCostClass(collectionId, data),
    onSuccess: (_d, { collectionId }) =>
      qc.invalidateQueries({ queryKey: keys.productCostClasses(collectionId) }),
  });
}

export function useUpdateProductCostClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      pccId,
      data,
    }: {
      collectionId: string;
      pccId: string;
      data: Record<string, unknown>;
    }) => pcApi.updateProductCostClass(collectionId, pccId, data),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.productCostClasses(collectionId) });
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
    },
  });
}

export function useDeleteProductCostClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, pccId }: { collectionId: string; pccId: string }) =>
      pcApi.deleteProductCostClass(collectionId, pccId),
    onSuccess: (_d, { collectionId }) => {
      qc.invalidateQueries({ queryKey: keys.productCostClasses(collectionId) });
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
    },
  });
}

export function useToggleProductCostClassActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, pccId }: { collectionId: string; pccId: string }) =>
      pcApi.toggleProductCostClassActive(collectionId, pccId),
    onSuccess: (_d, { collectionId }) =>
      qc.invalidateQueries({ queryKey: keys.productCostClasses(collectionId) }),
  });
}

// ==========================================
// Product Document mutation hooks
// ==========================================

export function useSaveMsProductDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      productId,
      data,
    }: {
      collectionId: string;
      productId: string;
      data: Parameters<typeof saveMsDocument>[2];
    }) => saveMsDocument(collectionId, productId, data),
    onSuccess: (_d, { collectionId, productId }) => {
      qc.invalidateQueries({ queryKey: keys.productDocumentsOverview(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productDocuments(collectionId, productId) });
    },
  });
}

export function useSaveUploadedProductDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      productId,
      data,
    }: {
      collectionId: string;
      productId: string;
      data: Parameters<typeof saveUploadedDocument>[2];
    }) => saveUploadedDocument(collectionId, productId, data),
    onSuccess: (_d, { collectionId, productId }) => {
      qc.invalidateQueries({ queryKey: keys.productDocumentsOverview(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productDocuments(collectionId, productId) });
    },
  });
}

export function useDeleteProductDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      productId,
      documentId,
    }: {
      collectionId: string;
      productId: string;
      documentId: string;
    }) => deleteProductDocument(collectionId, productId, documentId),
    onSuccess: (_d, { collectionId, productId }) => {
      qc.invalidateQueries({ queryKey: keys.productDocumentsOverview(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productDocuments(collectionId, productId) });
    },
  });
}

export function useRefreshMsProductDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      productId,
      documentId,
    }: {
      collectionId: string;
      productId: string;
      documentId: string;
    }) => refreshMsDocument(collectionId, productId, documentId),
    onSuccess: (_d, { collectionId, productId }) => {
      qc.invalidateQueries({ queryKey: keys.productDocumentsOverview(collectionId) });
      qc.invalidateQueries({ queryKey: keys.productDocuments(collectionId, productId) });
    },
  });
}

export function useStartProductImport(collectionId: string | null) {
  return useMutation({
    mutationFn: ({ rows }: { rows: ProductImportRow[] }) =>
      pcApi.startProductImport(collectionId!, { rows }),
  });
}

export function useProductImportJob(collectionId: string | null, jobId: string | null) {
  const qc = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForCollectionEdit();
  const { data, error, isLoading } = useQuery<ProductImportJob>({
    queryKey: ["productImportJob", collectionId, jobId] as const,
    queryFn: () => pcApi.getProductImportJob(collectionId!, jobId!),
    enabled: !!collectionId && !!jobId,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === "pending" || status === "processing" ? 1500 : false;
    },
  });

  const status = data?.status;
  useEffect(() => {
    if (status === "done") {
      qc.invalidateQueries({ queryKey: keys.products(collectionId) });
      // Imported products start without wrapper types — the collection
      // detail's wrapperTypeStatus (tab warning icons) goes stale with them.
      qc.invalidateQueries({ queryKey: keys.detail(collectionId) });
      // The job runs invalidateDynamicForCollection once at the end.
      invalidateFlowCaches();
    }
  }, [status, collectionId, qc, invalidateFlowCaches]);

  return { job: data ?? null, error, isLoading };
}
